import { useMemo } from 'react'
import { router, useForm } from '@inertiajs/react'
import { CreditCard, ExternalLink, Gift, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { BillingPlanOption, DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  worker: DigitalWorkerDetail
  plans: BillingPlanOption[]
  canManage: boolean
}

const STATUS_LABELS: Record<string, string> = {
  incomplete: 'Aguardando pagamento',
  incomplete_expired: 'Checkout expirado',
  trialing: 'Período de teste',
  active: 'Ativa',
  past_due: 'Pagamento pendente',
  paused: 'Pausada',
  canceled: 'Cancelada',
  unpaid: 'Inadimplente',
}

function formatCurrency(cents: number, currency: string) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(cents / 100)
}

export default function SubscriptionStatusCard({ worker, plans, canManage }: Props) {
  const purchasablePlans = useMemo(() => plans.filter((plan) => plan.purchasable), [plans])
  const defaultSlug = useMemo(() => {
    if (worker.billing.plan_slug && purchasablePlans.some((plan) => plan.slug === worker.billing.plan_slug)) {
      return worker.billing.plan_slug
    }

    return purchasablePlans[0]?.slug || ''
  }, [purchasablePlans, worker.billing.plan_slug])
  const form = useForm({ plan_slug: defaultSlug })
  const portalForm = useForm({})
  const selectedPlan = purchasablePlans.find((plan) => plan.slug === form.data.plan_slug)
  const canChangePlan = canManage && worker.billing.can_change_plan && purchasablePlans.some((plan) => plan.slug !== worker.billing.plan_slug)
  const canMigrateToPaid = canManage && worker.billing.can_migrate_to_paid
  const canManagePortal = canManage && worker.billing.can_manage_portal

  const handleSubscribe = () => {
    if (!selectedPlan || !canManage) return
    form.post(`/dashboard/digital_workers/${worker.id}/checkout`, { preserveScroll: true })
  }

  const handleChangePlan = () => {
    if (!selectedPlan || !canChangePlan || selectedPlan.slug === worker.billing.plan_slug) return
    form.post(`/dashboard/digital_workers/${worker.id}/change_billing_plan`, { preserveScroll: true })
  }

  const handleMigrateToPaid = () => {
    if (!selectedPlan || !canMigrateToPaid) return
    form.post(`/dashboard/digital_workers/${worker.id}/migrate_to_paid_subscription`, { preserveScroll: true })
  }

  const handleOpenPortal = () => {
    if (!canManagePortal) return
    portalForm.post(`/dashboard/digital_workers/${worker.id}/billing_portal`, { preserveScroll: true })
  }

  const handleReload = () => router.reload({ only: ['worker'] })

  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h2 className="text-[14px] font-semibold text-foreground">Assinatura</h2>
          <p className="text-[13px] text-muted-foreground">
            {worker.billing.active
              ? `${worker.billing.plan_name} · ${worker.billing.source === 'manual' ? 'Cortesia' : 'Stripe'}`
              : worker.billing.status
              ? STATUS_LABELS[worker.billing.status] || worker.billing.status
              : 'Aguardando assinatura'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {worker.billing.source === 'manual' && <Gift className="h-4 w-4 text-success" />}
          {/* Pill de status no padrão da ficha: ok-soft quando ativa. */}
          <span
            className={`rounded-full px-[9px] py-0.5 text-[11px] font-semibold ${
              worker.billing.active ? 'bg-success/12 text-success' : 'bg-muted text-muted-foreground'
            }`}
          >
            {worker.billing.active ? 'Ativa' : 'Pendente'}
          </span>
        </div>
      </div>

      {!worker.billing.active && canManage && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <select
            value={form.data.plan_slug}
            onChange={(event) => form.setData('plan_slug', event.target.value)}
            disabled={form.processing || purchasablePlans.length === 0}
            className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {purchasablePlans.map((plan) => (
              <option key={plan.slug} value={plan.slug}>
                {plan.name} · {formatCurrency(plan.price_cents, plan.currency)}
              </option>
            ))}
          </select>
          <Button type="button" onClick={handleSubscribe} disabled={form.processing || !selectedPlan}>
            {form.processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            {worker.billing.status === 'canceled' ? 'Assinar novamente' : 'Assinar'}
          </Button>
        </div>
      )}

      {worker.billing.active && canChangePlan && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <select
            value={form.data.plan_slug}
            onChange={(event) => form.setData('plan_slug', event.target.value)}
            disabled={form.processing || purchasablePlans.length === 0}
            className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {purchasablePlans.map((plan) => (
              <option key={plan.slug} value={plan.slug}>
                {plan.name} · {formatCurrency(plan.price_cents, plan.currency)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            onClick={handleChangePlan}
            disabled={form.processing || !selectedPlan || selectedPlan.slug === worker.billing.plan_slug}
          >
            {form.processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            Alterar plano
          </Button>
        </div>
      )}

      {canMigrateToPaid && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <select
            value={form.data.plan_slug}
            onChange={(event) => form.setData('plan_slug', event.target.value)}
            disabled={form.processing || purchasablePlans.length === 0}
            className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {purchasablePlans.map((plan) => (
              <option key={plan.slug} value={plan.slug}>
                {plan.name} · {formatCurrency(plan.price_cents, plan.currency)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            onClick={handleMigrateToPaid}
            disabled={form.processing || !selectedPlan}
          >
            {form.processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            Migrar para assinatura paga
          </Button>
        </div>
      )}

      {canManagePortal && (
        <Button
          type="button"
          variant="outline"
          className="mt-3"
          onClick={handleOpenPortal}
          disabled={portalForm.processing}
        >
          {portalForm.processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
          Gerenciar assinatura
        </Button>
      )}

      {!worker.billing.active && worker.billing.checkout_session_status && (
        <Button type="button" variant="ghost" size="sm" className="mt-3 px-0" onClick={handleReload}>
          <RefreshCw className="h-4 w-4" />
          Atualizar status
        </Button>
      )}
    </section>
  )
}
